import { blue, red } from "@ant-design/colors";
import { Card, Col, Modal, Row, Space } from "antd";
import moment from "moment";
import React, { memo, useState } from "react";
import { useIntl } from "react-intl";

import { TMessage } from "@/api";
import { useDirection } from "@/hooks";

import { DeleteMessage, DownloadMedia } from "./actions";
import { IChatProps } from "./types";

interface IMessageProps extends Omit<IChatProps, "conversation"> {
  item: TMessage;
}

export const Message = memo(({ item, state, player }: IMessageProps) => {
  const intl = useIntl();
  const isFromPlayer = item.from.userId === player.id;
  const isDeleted = Boolean(item.deletedAt);
  const dir = useDirection();
  const [modalOpen, setModalOpen] = useState(false);

  const renderContent = () => {
    if (isDeleted) {
      return <del>{item.text}</del>;
    }

    if (item.type === "image") {
      return (
        <img
          src={`/im/attachment/${item.text}`}
          alt="Attachment"
          style={{
            width: "142px",
            height: "100px",
            objectFit: "contain",
            borderRadius: "8px",
            cursor: "pointer",
          }}
          onClick={() => setModalOpen(true)}
        />
      );
    }

    if (item.type === "video") {
      return (
        <div
          style={{
            position: "relative",
            width: "142px",
            height: "100px",
            cursor: "pointer",
          }}
          onClick={() => setModalOpen(true)}
        >
          <video
            src={`/im/attachment/${item.text}`}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "8px",
            }}
            muted
          />
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              backgroundColor: "rgba(0, 0, 0, 0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
            }}
          >
            <div
              style={{
                width: 0,
                height: 0,
                borderLeft: "14px solid white",
                borderTop: "8px solid transparent",
                borderBottom: "8px solid transparent",
                marginLeft: "3px",
              }}
            />
          </div>
        </div>
      );
    }

    return item.text;
  };

  return (
    <>
      <Row
        justify={isFromPlayer ? "start" : "end"}
        style={{ marginBottom: "8px" }}
      >
        <Col>
          <Space
            size={[2, 2]}
            direction="vertical"
            style={{ position: "relative" }}
          >
            <Card
              style={{
                background: isFromPlayer ? blue[2] : "#d3d3d3",
                borderRadius: "12px",
                maxWidth: "400px",
                wordBreak: "break-word",
                opacity: isDeleted ? 0.5 : 1,
              }}
              aria-label={intl.formatMessage({ defaultMessage: "Message" })}
            >
              {renderContent()}
            </Card>
            <div
              style={{
                whiteSpace: "nowrap",
                opacity: 0.8,
                display: "flex",
                alignItems: "center",
                justifyContent: isFromPlayer ? "flex-start" : "flex-end",
                fontSize: 10,
                ...(dir === "rtl"
                  ? { marginLeft: "8px", unicodeBidi: "plaintext" }
                  : { marginRight: "8px" }),
              }}
            >
              <span style={{ fontSize: 10 }}>{item.from.userName}</span>
              {" | "}
              <span style={{ fontSize: 10 }}>
                {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
              </span>

              {isDeleted ? (
                <span
                  style={{
                    color: red[4],
                    fontSize: "12px",
                    ...(dir === "rtl"
                      ? { marginRight: "8px" }
                      : { marginLeft: "8px" }),
                  }}
                >
                  {intl
                    .formatMessage({ defaultMessage: "Deleted" })
                    .toUpperCase()}
                </span>
              ) : (
                <>
                  <DownloadMedia item={item} />
                  <DeleteMessage state={state} item={item} />
                </>
              )}
            </div>
          </Space>
        </Col>
      </Row>

      <Modal
        className="im-message-attachment-modal"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width="800px"
        title={item.text}
        destroyOnClose
      >
        {item.type === "image" && (
          <img
            src={`/im/attachment/${item.text}`}
            alt="Attachment"
            style={{
              width: "auto",
              maxHeight: "80vh",
              objectFit: "contain",
            }}
          />
        )}
        {item.type === "video" && (
          <video
            src={`/im/attachment/${item.text}`}
            controls
            autoPlay={false}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
            }}
          />
        )}
      </Modal>
    </>
  );
});

Message.displayName = "Message";
